import { useState } from 'react';
import { supplierAPI } from '../../api';
import '../../styles/ModernUI.css';

// Import modular components
import SupplierInfo from './SupplierInfo';
import ProcessingOptions from './ProcessingOptions';
import ImageUpload from '../common/ImageUpload';
import ProcessButton from '../common/ProcessButton';

const DocumentProcessor = ({ onDocumentProcessed }) => {
  // ===== STATE MANAGEMENT =====
  const [annotatedImageFiles, setAnnotatedImageFiles] = useState([]);
  const [imageDragOver, setImageDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [successfulSubmit, setSuccessfulSubmit] = useState(false);
  
  const [formData, setFormData] = useState({
    supplierName: '',
    additionalText: '',
    annotatedImages: false,
    processAllPages: false
  });

  // ===== IMAGE FILE HANDLERS =====
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setAnnotatedImageFiles(prev => [...prev, ...imageFiles]);
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setImageDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setAnnotatedImageFiles(prev => [...prev, ...imageFiles]);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    setImageDragOver(true);
  };

  const handleImageDragLeave = (e) => {
    e.preventDefault();
    setImageDragOver(false);
  };

  const removeImageFile = (index) => {
    setAnnotatedImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // ===== FORM HANDLERS =====
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear image files when annotated images toggle is disabled
    if (field === 'annotatedImages' && !value) {
      setAnnotatedImageFiles([]);
    }
  };

  // ===== MESSAGE HANDLING =====
  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000); // Auto-hide after 5 seconds
  };

  const hideMessage = () => {
    setMessage(null);
  };

  // ===== VALIDATION =====
  const validateForm = () => {
    if (!formData.supplierName.trim()) {
      showMessage('Please enter a supplier name', 'error');
      return false;
    }
    if (formData.annotatedImages && annotatedImageFiles.length === 0) {
      showMessage('Please upload at least one image when annotated images is enabled', 'error');
      return false;
    }
    return true;
  };



  // ===== DOCUMENT PROCESSING =====
  const processDocuments = async () => {
    if (!validateForm()) return;

    setProcessing(true);

    try {
      // Create FormData to send images and data together
      const formDataPayload = new FormData();
      
      // Append form fields as strings
      formDataPayload.append('supplierName', formData.supplierName);
      formDataPayload.append('additionalText', formData.additionalText || '');
      formDataPayload.append('isAnnotatedImages', String(formData.annotatedImages));
      formDataPayload.append('processAllPages', String(formData.processAllPages));
      formDataPayload.append('processedAt', new Date().toISOString());
      
      // CRITICAL: Append images with the key "images" for each file
      // Backend expects: images: List[UploadFile]
      if (formData.annotatedImages && annotatedImageFiles.length > 0) {
        console.log(`Adding ${annotatedImageFiles.length} images to request...`);
        annotatedImageFiles.forEach((file, index) => {
          // Each file must be appended with the same key "images"
          formDataPayload.append('images', file, file.name);
          console.log(`Added image ${index + 1}/${annotatedImageFiles.length}: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
        });
      }
      
      // Log FormData contents for debugging
      console.log('FormData contents:');
      for (let pair of formDataPayload.entries()) {
        if (pair[1] instanceof File) {
          console.log(`  ${pair[0]}: [File] ${pair[1].name} (${pair[1].type})`);
        } else {
          console.log(`  ${pair[0]}: ${pair[1]}`);
        }
      }
      
      console.log('Sending request to backend...');
      
      // Send to backend API using centralized API service
      const result = await supplierAPI.processDocuments(formDataPayload);
      
      // Create result for UI display
      const displayResult = {
        id: result.id || Date.now(),
        supplier: formData.supplierName,
        additionalText: formData.additionalText,
        timestamp: new Date().toLocaleString(),
        annotatedImages: formData.annotatedImages,
        processAllPages: formData.processAllPages,
        uploadedImages: annotatedImageFiles.length,
        imageReferences: result.imageReferences || [],
        status: 'processed'
      };
      
      onDocumentProcessed(displayResult);
      
      // Reset form after successful processing
      resetForm();
      setSuccessfulSubmit(true);
      showMessage('Supplier added successfully! Redirecting to suppliers list...', 'success');
      
      // Redirect to suppliers page after 2 seconds
      setTimeout(() => {
        window.location.href = '/suppliers';
      }, 2000);
      
    } catch (error) {
      console.error('Processing error:', error);
      showMessage(`Error processing documents: ${error.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  // ===== UTILITY FUNCTIONS =====
  const resetForm = () => {
    setAnnotatedImageFiles([]);
    setFormData({
      supplierName: '',
      additionalText: '',
      annotatedImages: false,
      processAllPages: false
    });
  };

  // ===== MESSAGE COMPONENT =====
  const MessageDisplay = () => {
    if (!message) return null;

    return (
      <div className={`message-banner ${message.type}`}>
        <div className="message-content">
          <span className="message-icon">
            {message.type === 'success' && '✅'}
            {message.type === 'error' && '❌'}
            {message.type === 'info' && 'ℹ️'}
          </span>
          <span className="message-text">{message.text}</span>
          <button className="message-close" onClick={hideMessage}>
            ✕
          </button>
        </div>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div className="document-processor">
      <div className="page-header-simple">
        <h1>📄 Add New Supplier</h1>
        <p className="page-subtitle">Configure supplier and upload annotated invoice images</p>
      </div>
      
      <MessageDisplay />
      
      <div className="form-container">
        <SupplierInfo 
          formData={formData}
          onFormChange={handleFormChange}
        />
        
        <ProcessingOptions 
          formData={formData}
          onFormChange={handleFormChange}
        />
        
        {formData.annotatedImages && (
          <ImageUpload 
            annotatedImageFiles={annotatedImageFiles}
            imageDragOver={imageDragOver}
            onImageSelect={handleImageSelect}
            onImageDrop={handleImageDrop}
            onImageDragOver={handleImageDragOver}
            onImageDragLeave={handleImageDragLeave}
            onRemoveImageFile={removeImageFile}
          />
        )}
        
        <ProcessButton 
          processing={processing}
          formData={formData}
          annotatedImageFiles={annotatedImageFiles}
          onProcessDocuments={processDocuments}
          successfulSubmit={successfulSubmit}
        />
        
        {!successfulSubmit && (
          <div className="form-actions-secondary">
            <button 
              type="button" 
              className="btn-cancel"
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentProcessor;
